import React, { useState } from 'react';
import styled from 'styled-components';
import { MessageSquare, X } from 'lucide-react';
import { theme } from '../styles/GlobalStyles';
import CoachSidebar from './CoachSidebar';

const FabButton = styled.button`
  position: fixed;
  bottom: ${theme.spacing['2xl']};
  right: ${theme.spacing['2xl']};
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  background: var(--color-primary);
  border: none;
  color: var(--color-white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 96, 100, 0.4), 0 0 20px rgba(0, 96, 100, 0.2);
  z-index: 1000;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 96, 100, 0.5), 0 0 30px rgba(0, 96, 100, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    bottom: ${theme.spacing.xl};
    right: ${theme.spacing.xl};
    width: 56px;
    height: 56px;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 999;
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  pointer-events: ${({ isOpen }) => isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const Drawer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80vh;
  max-height: 800px;
  background: var(--color-surface);
  border-top-left-radius: ${theme.borderRadius['3xl']};
  border-top-right-radius: ${theme.borderRadius['3xl']};
  border-top: 1px solid var(--color-border);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transform: ${({ isOpen }) => isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  
  @media (min-width: ${theme.breakpoints.desktop}) {
    left: auto;
    width: 420px;
    right: ${theme.spacing['2xl']};
    bottom: ${theme.spacing['2xl']};
    height: calc(100vh - 180px);
    max-height: 700px;
    border-radius: ${theme.borderRadius['2xl']};
    border-top-left-radius: ${theme.borderRadius['2xl']};
    border-top-right-radius: ${theme.borderRadius['2xl']};
  }
`;

const DrawerHeader = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const DrawerTitle = styled.h3`
  font-size: ${theme.fontSize.xl};
  font-weight: ${theme.fontWeight.bold};
  color: var(--color-text);
  font-family: 'Inter', sans-serif;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  background: transparent;
  border: none;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-surface-hover);
    color: var(--color-text);
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CoachFab: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <FabButton onClick={handleToggle} aria-label="Abrir Coach IA">
        {isOpen ? <X size={24} /> : <MessageSquare size={24} />}
      </FabButton>
      
      {isOpen && (
        <>
          <Overlay isOpen={isOpen} onClick={handleClose} />
          <Drawer isOpen={isOpen}>
            <DrawerHeader>
              <DrawerTitle>Coach IA</DrawerTitle>
              <CloseButton onClick={handleClose} aria-label="Fechar">
                <X size={20} />
              </CloseButton>
            </DrawerHeader>
            <DrawerContent>
              <CoachSidebar />
            </DrawerContent>
          </Drawer>
        </>
      )}
    </>
  );
};

export default CoachFab;

